import { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { bffPermissionService, bffConfigService } from '@resort-os/api-client';
import { filterAppsBySystemConfig, getAppKeyFromUrl } from '@resort-os/config';
import { useAuth } from '@resort-os/ui';

const AUTH_APP_URL = import.meta.env.VITE_AUTH_APP_URL || 'http://localhost:3000';

interface PermissionGateProps {
  children: React.ReactNode;
  fallbackUrl?: string;
}

export function PermissionGate({
  children,
  fallbackUrl = `${AUTH_APP_URL}/dashboard`,
}: PermissionGateProps) {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const checkPermission = async () => {
      if (authLoading) return;

      if (!isAuthenticated || !user?.role) {
        window.location.replace(fallbackUrl);
        return;
      }

      try {
        const currentAppKey = getAppKeyFromUrl(window.location.href);

        if (!currentAppKey || currentAppKey === 'auth') {
          if (!cancelled) setAllowed(true);
          if (!cancelled) setChecking(false);
          return;
        }

        const [appsRes, configsRes] = await Promise.all([
          bffPermissionService.getAppsForRole(user.role),
          bffConfigService.getAll().catch(() => ({ configs: [] })),
        ]);
        const allowedApps = filterAppsBySystemConfig(
          appsRes.data.apps || [],
          configsRes.configs || [],
        );

        if (!allowedApps.includes(currentAppKey)) {
          window.location.replace(fallbackUrl);
          return;
        }

        if (!cancelled) setAllowed(true);
      } catch (e) {
        console.error(e);
        window.location.replace(fallbackUrl);
      } finally {
        if (!cancelled) setChecking(false);
      }
    };

    checkPermission();

    return () => {
      cancelled = true;
    };
  }, [user, isAuthenticated, authLoading]);

  if (authLoading || checking) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Box textAlign="center">
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography color="text.secondary">Checking permissions...</Typography>
        </Box>
      </Box>
    );
  }

  if (!allowed) {
    return null;
  }

  return <>{children}</>;
}
